import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash, CalendarBlank, ListChecks } from '@phosphor-icons/react';
import { useConfirmDelete } from '@/components/ConfirmDialogs';
import WeekView from '@/components/tarefas/WeekView';
import { ListView, ArchiveView } from '@/components/tarefas/ListArchiveViews';
import { fmtDate } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL || '';

const PRIORITY_LABELS = { baixa: 'Baixa', media: 'Média', alta: 'Alta' };
const PRIORITY_STYLES = { baixa: 'bg-gray-100 text-gray-600', media: 'bg-blue-50 text-blue-700', alta: 'bg-red-50 text-red-700' };
const STATUS_LABELS = { por_fazer: 'Por fazer', em_curso: 'Em curso', concluida: 'Concluída' };

export default function TarefasPage() {
  const { user } = useAuth();
  const confirmDelete = useConfirmDelete();
  const [team, setTeam] = useState([]);
  const [manualScope, setManualScope] = useState('mine');
  const [manualTasks, setManualTasks] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'media' });
  const [loading, setLoading] = useState(true);

  const loadTeam = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/task-assignees`);
      const real = (data || []).filter(s => !(s.email || '').toLowerCase().includes('test') && !(s.name || '').toLowerCase().startsWith('test'));
      setTeam(real);
    } catch { setTeam([]); }
  }, []);

  const loadManualTasks = useCallback(async () => {
    try { const { data } = await axios.get(`${API}/api/tasks?scope=${manualScope}`); setManualTasks(data || []); } catch { setManualTasks([]); }
  }, [manualScope]);

  useEffect(() => { loadTeam(); }, [loadTeam]);
  useEffect(() => { setLoading(true); loadManualTasks().finally(() => setLoading(false)); }, [loadManualTasks]);

  const nameFor = (uid) => team.find(t => t.user_id === uid)?.name || uid;

  const openCreate = () => { setEditingTask(null); setForm({ title: '', description: '', assigned_to: user?.user_id || '', due_date: '', priority: 'media' }); setShowForm(true); };
  const openEdit = (t) => { setEditingTask(t); setForm({ title: t.title, description: t.description || '', assigned_to: t.assigned_to, due_date: t.due_date || '', priority: t.priority }); setShowForm(true); };

  const submitForm = async () => {
    if (!form.title.trim()) { toast.error('O título é obrigatório'); return; }
    if (!form.assigned_to) { toast.error('Escolhe um responsável'); return; }
    try {
      if (editingTask) {
        await axios.put(`${API}/api/tasks/${editingTask.id}`, form);
        toast.success('Tarefa atualizada');
      } else {
        await axios.post(`${API}/api/tasks`, form);
        toast.success('Tarefa criada');
      }
      setShowForm(false);
      loadManualTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao guardar tarefa'); }
  };

  const changeStatus = async (task, status) => {
    try { await axios.put(`${API}/api/tasks/${task.id}`, { status }); loadManualTasks(); } catch { toast.error('Erro ao atualizar estado'); }
  };

  const removeTask = async (taskId) => {
    const t = manualTasks.find(x => x.id === taskId);
    const ok = await confirmDelete({ description: `Vai apagar a tarefa "${t?.title || ''}".` });
    if (!ok) return;
    try { await axios.delete(`${API}/api/tasks/${taskId}`); toast.success('Tarefa eliminada'); loadManualTasks(); } catch { toast.error('Erro ao eliminar tarefa'); }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto" data-testid="tarefas-page">
        <h1 className="font-heading text-2xl text-brand-text mb-1 flex items-center gap-2"><ListChecks size={24} /> Tarefas</h1>
        <p className="text-sm text-brand-muted mb-6">Tarefas ligadas a cada consulta (vista semanal) e tarefas manuais da equipa.</p>

        <Tabs defaultValue={user?.role === 'colaborador' ? 'manuais' : 'semana'} className="w-full">
          <TabsList className="bg-white border border-brand-border rounded-2xl p-1 mb-4" data-testid="tarefas-tabs">
            {user?.role !== 'colaborador' && (<>
              <TabsTrigger value="semana" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-semana">Semana</TabsTrigger>
              <TabsTrigger value="lista" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-lista">Lista</TabsTrigger>
            </>)}
            <TabsTrigger value="manuais" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-manuais">Manuais</TabsTrigger>
            {user?.role !== 'colaborador' && (
              <TabsTrigger value="arquivo" className="text-xs px-3 py-2 rounded-xl data-[state=active]:bg-brand-green data-[state=active]:text-white" data-testid="tab-arquivo">Arquivo</TabsTrigger>
            )}
          </TabsList>

          {user?.role !== 'colaborador' && (<>
            <TabsContent value="semana"><WeekView /></TabsContent>
            <TabsContent value="lista"><ListView /></TabsContent>
            <TabsContent value="arquivo"><ArchiveView /></TabsContent>
          </>)}

          <TabsContent value="manuais">
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-1 bg-white border border-brand-border rounded-xl p-1">
                <button onClick={() => setManualScope('mine')} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${manualScope === 'mine' ? 'bg-brand-green text-white' : 'text-brand-muted'}`} data-testid="scope-mine-btn">Minhas</button>
                <button onClick={() => setManualScope('team')} className={`text-xs px-3 py-1.5 rounded-lg transition-colors ${manualScope === 'team' ? 'bg-brand-green text-white' : 'text-brand-muted'}`} data-testid="scope-team-btn">Equipa</button>
              </div>
              <Button size="sm" className="bg-brand-green text-white text-xs gap-1" onClick={openCreate} data-testid="new-task-btn"><Plus size={14} /> Nova Tarefa</Button>
            </div>
            {loading ? (
              <p className="text-sm text-brand-placeholder text-center py-8">A carregar…</p>
            ) : manualTasks.length === 0 ? (
              <p className="text-sm text-brand-placeholder text-center py-8" data-testid="manual-tasks-empty">Sem tarefas.</p>
            ) : (
              <div className="space-y-2">
                {manualTasks.map(t => (
                  <div key={t.id} className="bg-white border border-brand-border rounded-2xl p-3.5" data-testid={`manual-task-${t.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1 cursor-pointer" onClick={() => openEdit(t)}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-medium text-brand-text">{t.title}</p>
                          <span className={`text-[10px] px-2 py-0.5 rounded-full ${PRIORITY_STYLES[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
                        </div>
                        {t.description && <p className="text-xs text-brand-muted mt-0.5">{t.description}</p>}
                        <div className="flex items-center gap-3 mt-1.5 text-[11px] text-brand-placeholder">
                          <span>{nameFor(t.assigned_to)}</span>
                          {t.due_date && <span className="flex items-center gap-1"><CalendarBlank size={12} /> {fmtDate(t.due_date)}</span>}
                        </div>
                      </div>
                      <button onClick={() => removeTask(t.id)} className="text-brand-placeholder hover:text-red-500 flex-shrink-0" data-testid={`delete-task-btn-${t.id}`}><Trash size={16} /></button>
                    </div>
                    <div className="flex gap-1 mt-2">
                      {['por_fazer', 'em_curso', 'concluida'].map(s => (
                        <button key={s} onClick={() => changeStatus(t, s)} className={`text-[10px] px-2 py-1 rounded-full transition-colors ${t.status === s ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted hover:bg-brand-border'}`} data-testid={`status-btn-${s}-${t.id}`}>{STATUS_LABELS[s]}</button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>

        <Dialog open={showForm} onOpenChange={setShowForm}>
          <DialogContent className="rounded-3xl border-brand-border" data-testid="task-form-modal">
            <DialogHeader><DialogTitle className="font-heading text-brand-text">{editingTask ? 'Editar Tarefa' : 'Nova Tarefa'}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <Input placeholder="Título" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} data-testid="task-title-input" />
              <Textarea placeholder="Descrição (opcional)" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} data-testid="task-description-input" />
              <Select value={form.assigned_to} onValueChange={v => setForm(f => ({ ...f, assigned_to: v }))}>
                <SelectTrigger data-testid="task-assignee-select"><SelectValue placeholder="Responsável" /></SelectTrigger>
                <SelectContent>{team.map(m => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}</SelectContent>
              </Select>
              <div className="flex gap-3">
                <Input type="date" value={form.due_date} onChange={e => setForm(f => ({ ...f, due_date: e.target.value }))} data-testid="task-due-date-input" />
                <Select value={form.priority} onValueChange={v => setForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button className="w-full bg-brand-green text-white" onClick={submitForm} data-testid="task-form-submit-btn">{editingTask ? 'Guardar' : 'Criar Tarefa'}</Button>
            </div>
          </DialogContent>
        </Dialog>

      </div>
  );
}
